import React, { useState } from 'react';
import {
  View,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  KeyboardAvoidingView,
  Linking,
  Alert,
  Platform,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { LoginHeroHeader } from '@/components/LoginHeroHeader';
import { strings } from '@/constants/residents/strings';
import { APP_CONSTANTS } from '@/constants/residents';
import { onColor } from '@/constants/shared/theme/onColor';
import { AppConfig } from '@/constants/residents/config';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { AuthStackParamList } from '@/navigation/residents/mobile/AuthNavigator';
import { getUserTypeOptions, isResident } from '@/types/residents/userType';
import { UserType } from '@/types/residents/user';
import { showAlert } from '@/utils/common/alert';

type UserSelectionNavigationProp = StackNavigationProp<AuthStackParamList, 'UserSelection'>;

export const UserSelectionScreen: React.FC = () => {
  const [selectedUserType, setSelectedUserType] = useState<UserType | null>(null);
  const navigation = useNavigation<UserSelectionNavigationProp>();
  const styles = useThemedStyles(makeStyles);

  const userTypeOptions = getUserTypeOptions();

  const handleUserTypeSelection = (userType: UserType) => {
    setSelectedUserType(userType);
  };

  const handleContinue = () => {
    if (!selectedUserType) return;

    if (isResident(selectedUserType)) {
      // Navigate to signup screen for residents
      navigation.navigate('Signup');
    } else {
      // Open website for non-residents
      handleOpenWebsite();
    }
  };

  const handleOpenWebsite = () => {
    const websiteUrl = AppConfig.URLS.APP; // Environment-aware URL matching iOS
    
    Linking.canOpenURL(websiteUrl)
      .then((supported) => {
        if (supported) {
          Linking.openURL(websiteUrl);
        } else {
          showAlert('Error', 'Unable to open website');
        }
      })
      .catch(() => {
        showAlert('Error', 'Unable to open website');
      });
  };

  const handlePrivacyPolicy = () => {
    navigation.navigate('Legal', { contentType: 'privacy' });
  };

  const handleTermsOfService = () => {
    navigation.navigate('Legal', { contentType: 'terms' });
  };

  const getButtonTitle = () => {
    if (!selectedUserType) return '';
    return isResident(selectedUserType) 
      ? strings.userSelection.button.continue 
      : strings.userSelection.button.web;
  };

  const showNonResidentInfo = selectedUserType && !isResident(selectedUserType);

  return (
    <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        <LoginHeroHeader
          title={strings.userSelection.header.title}
          subtitle={strings.userSelection.header.subtitle}
        />

        <View style={styles.content}>
          <Text style={styles.sectionSubtitle}>
            {strings.userSelection.section.subtitle}
          </Text>

          <View style={styles.optionsContainer}>
            {userTypeOptions.map((option) => (
              <TouchableOpacity
                key={option.type}
                style={[
                  styles.optionCard,
                  selectedUserType === option.type && styles.optionCardSelected
                ]}
                onPress={() => handleUserTypeSelection(option.type)}
              >
                <View style={styles.optionContent}>
                  <View style={styles.optionTextContainer}>
                    <Text style={styles.optionTitle}>{option.title}</Text>
                    <Text style={styles.optionSubtitle}>{option.subtitle}</Text>
                  </View>
                  <View style={[
                    styles.checkbox,
                    selectedUserType === option.type && styles.checkboxSelected
                  ]}>
                    {selectedUserType === option.type && (
                      <Text style={styles.checkmark}>✓</Text>
                    )}
                  </View>
                </View>
              </TouchableOpacity>
            ))}
          </View>

          {/* Non-resident info section */}
          {showNonResidentInfo && (
            <View style={styles.nonResidentInfo}>
              <Text style={styles.nonResidentText}>
                {strings.userSelection.section.web}
              </Text>
            </View>
          )}

          {/* Continue/Website button */}
          {selectedUserType && (
            <TouchableOpacity
              style={styles.continueButton}
              onPress={handleContinue}
            >
              <Text style={styles.continueButtonText}>
                {getButtonTitle()}
              </Text>
            </TouchableOpacity>
          )}

          {/* Legal Footer */}
          <View style={styles.legalFooter}>
            <View style={styles.legalButtonsContainer}>
              <TouchableOpacity style={styles.legalButton} onPress={handlePrivacyPolicy}>
                <Text style={styles.legalButtonText}>Privacy Policy</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.legalButton} onPress={handleTermsOfService}>
                <Text style={styles.legalButtonText}>Terms of Service</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const makeStyles = ({ colors, semantic }: ThemeContextValue) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg.base,
  },
  scrollContent: {
    flexGrow: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  sectionSubtitle: {
    fontSize: 13,
    color: colors.text.secondary,
    textAlign: 'center',
    marginBottom: 16,
    fontFamily: Platform.select({
      ios: 'Avenir-Book',
      android: 'sans-serif',
      default: 'System',
    }),
  },
  optionsContainer: {
    marginBottom: 20,
  },
  optionCard: {
    backgroundColor: colors.card.bg,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: semantic.border.secondary,
    marginBottom: 16,
    padding: 16,
  },
  optionCardSelected: {
    borderColor: semantic.interactive.primary,
    borderWidth: 2,
  },
  optionContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  optionTextContainer: {
    flex: 1,
    marginRight: 12,
  },
  optionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: 4,
    fontFamily: Platform.select({
      ios: 'Avenir-Medium',
      android: 'sans-serif-medium',
      default: 'System',
    }),
  },
  optionSubtitle: {
    fontSize: 13,
    color: colors.text.secondary,
    lineHeight: 18,
    fontFamily: Platform.select({
      ios: 'Avenir-Book',
      android: 'sans-serif',
      default: 'System',
    }),
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: semantic.border.secondary,
    backgroundColor: colors.card.bg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    borderColor: semantic.interactive.primary,
    backgroundColor: semantic.interactive.primary,
  },
  checkmark: {
    // Sits on the selected checkbox fill, so derive from it.
    color: onColor(semantic.interactive.primary),
    fontSize: 14,
    fontWeight: 'bold',
  },
  nonResidentInfo: {
    backgroundColor: semantic.background.secondary,
    padding: 16,
    borderRadius: 8,
    marginBottom: 20,
  },
  nonResidentText: {
    fontSize: 13,
    color: colors.text.primary,
    textAlign: 'center',
    lineHeight: 18,
    fontFamily: Platform.select({
      ios: 'Avenir-Book',
      android: 'sans-serif',
      default: 'System',
    }),
  },
  continueButton: {
    backgroundColor: semantic.interactive.primary,
    borderRadius: APP_CONSTANTS.BUTTON_CORNER_RADIUS,
    paddingVertical: 16,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 30,
  },
  continueButtonText: {
    // Was the off-white TEXT_WHITE, which measures 3.99:1 on the brand fill —
    // under the AA floor. Deriving from the fill picks pure white at 4.53:1.
    color: onColor(semantic.interactive.primary),
    fontSize: 16,
    fontWeight: '600',
    fontFamily: Platform.select({
      ios: 'Avenir-Medium',
      android: 'sans-serif-medium',
      default: 'System',
    }),
  },
  legalFooter: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  legalButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  legalButton: {
    paddingHorizontal: 32,
    paddingVertical: 8,
  },
  legalButtonText: {
    fontSize: 13,
    color: colors.text.muted,
    textAlign: 'center',
    fontFamily: Platform.select({
      ios: 'Avenir-Book',
      android: 'sans-serif',
      default: 'System',
    }),
  },
});
